import { useMemo, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { connectorsApi, ConnectorDescriptor, ConnectorKind } from '@/lib/connectors';
import { dataSourcesApi, DataSource } from '@/lib/dataSources';
import { AppShell } from '@/components/app/AppShell';
import { useCurrentUser } from '@/hooks/useAuth';
import { ConfirmModal } from '@/components/ConfirmModal';

const PROVIDER_LABEL: Record<ConnectorKind, string> = {
  freee: 'freee 会計',
  moneyforward: 'MoneyForward Cloud',
  quickbooks: 'QuickBooks Online',
  xero: 'Xero',
  netsuite: 'NetSuite',
  salesforce: 'Salesforce',
};

export function ConnectorsPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<'all' | 'JP' | 'global'>('all');
  const { data: user, isLoading: userLoading } = useCurrentUser();
  if (!userLoading && user?.isAdmin !== true) {
    return <Navigate to={`/app/workspace/${wsId}`} replace />;
  }

  const { data: connectors = [], isLoading } = useQuery({
    queryKey: ['connectors'],
    queryFn: () => connectorsApi.list(),
    enabled: true,
  });

  const { data: dataSources = [] } = useQuery({
    queryKey: ['data-sources', wsId],
    queryFn: () => dataSourcesApi.list(wsId!),
    enabled: !!wsId,
  });

  const visible = filter === 'all' ? connectors : connectors.filter((c) => c.region === filter);
  const returnedStatus = searchParams.get('status');
  const returnedKind = searchParams.get('connector');
  const returnedError = searchParams.get('error');

  // Group connected data sources by connector kind so each provider card
  // can show its sync UI.
  const dsByKind = useMemo(() => {
    const m: Partial<Record<ConnectorKind, DataSource[]>> = {};
    for (const ds of dataSources) {
      const provider = ((ds.config as { provider?: string })?.provider ?? '') as ConnectorKind;
      if (!provider) continue;
      (m[provider] = m[provider] ?? []).push(ds);
    }
    return m;
  }, [dataSources]);

  return (
    <AppShell title={t('connectors.title')} subtitle={t('connectors.subtitle')}>
<main className="mx-auto max-w-5xl px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">{t('connectors.title')}</h1>
          <p className="mt-1 text-ink-600">{t('connectors.subtitle')}</p>
        </div>

        {returnedStatus === 'connected' && returnedKind && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {t('connectors.connected_to')} <span className="num font-semibold">{returnedKind}</span>{t('connectors.connected_after')}
          </div>
        )}
        {returnedError && (
          <div className="rounded-xl border border-warn-500/30 bg-warn-500/10 px-4 py-3 text-sm text-warn-600">
            {returnedError === 'unconfigured'
              ? t('connectors.unconfigured')
              : returnedError === 'requires_enterprise'
                ? t('connectors.requires_enterprise')
                : returnedError}
          </div>
        )}

        <div className="flex items-center gap-2 text-sm">
          {(['all', 'JP', 'global'] as const).map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={
                'px-3 py-1.5 rounded-md transition ' +
                (filter === k ? 'bg-accent-500 text-white font-medium' : 'text-ink-600 hover:bg-ink-100')
              }
            >
              {k === 'all' ? t('connectors.all_providers') : k === 'JP' ? t('connectors.region_jp') : t('connectors.region_global')}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="text-sm text-ink-500">{t('common.loading')}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visible.map((c) => (
              <ConnectorCard
                key={c.kind}
                connector={c}
                workspaceId={wsId!}
                connectedSources={dsByKind[c.kind] ?? []}
              />
            ))}
          </div>
        )}
      </main>
    </AppShell>
  );
}

function ConnectorCard({
  connector,
  workspaceId,
  connectedSources,
}: {
  connector: ConnectorDescriptor;
  workspaceId: string;
  connectedSources: DataSource[];
}) {
  const { t } = useTranslation();
  const label = PROVIDER_LABEL[connector.kind] || connector.label;
  const blurb = t(`connectors.provider_blurbs.${connector.kind}`);

  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5 hover:shadow-soft transition">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-ink-900">{label}</h3>
          <p className="mt-0.5 text-xs text-ink-500">
            <span className="rounded-md bg-ink-100 px-1.5 py-0.5">
              {connector.region === 'JP' ? t('connectors.region_jp') : t('connectors.region_global')}
            </span>
          </p>
        </div>
      </div>
      <p className="mt-3 text-sm text-ink-600">{blurb}</p>
      <div className="mt-4 flex gap-2">
        <a
          href={connectorsApi.startUrl(connector.kind, workspaceId)}
          className="px-3 py-1.5 rounded-md bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600 transition"
        >
          {t('connectors.connect')}
        </a>
        <a
          href={connector.partnerUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 text-xs hover:bg-ink-100"
        >
          {t('connectors.partner_docs')} ↗
        </a>
      </div>

      {connectedSources.length > 0 && (
        <div className="mt-5 pt-4 border-t border-ink-100 space-y-3">
          {connectedSources.map((ds) => (
            <SyncRow key={ds.id} dataSource={ds} wsId={workspaceId} />
          ))}
        </div>
      )}
    </div>
  );
}

function SyncRow({ dataSource, wsId }: { dataSource: DataSource; wsId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';
  const [periodStart, setPeriodStart] = useState(firstOfMonth);
  const [periodEnd, setPeriodEnd] = useState(today);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const sync = useMutation({
    mutationFn: () => connectorsApi.sync(dataSource.id, periodStart, periodEnd),
    onSuccess: (r) => {
      setMessage(t('connectors.sync_inserted', { count: r.inserted }));
      qc.invalidateQueries({ queryKey: ['data-sources'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e: Error) => setMessage(t('connectors.sync_failed', { error: e.message })),
  });

  const disconnect = useMutation({
    mutationFn: () => dataSourcesApi.remove(dataSource.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['data-sources'] });
    },
  });

  return (
    <>
    <div className="rounded-lg bg-ink-50 px-3 py-2 text-xs space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-ink-500 num">#{dataSource.id.slice(0, 8)} · {dataSource.status}</span>
        <button
          onClick={() => setConfirmDisconnect(true)}
          disabled={disconnect.isPending}
          className="text-[11px] text-ink-500 hover:text-warn-600 disabled:opacity-50"
        >
          {t('connectors.disconnect', { defaultValue: 'Disconnect' })}
        </button>
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <label className="flex flex-col gap-0.5">
          <span className="text-ink-500">{t('connectors.sync_period_start')}</span>
          <input
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            className="border border-ink-300 rounded px-2 py-1 num"
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-ink-500">{t('connectors.sync_period_end')}</span>
          <input
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
            className="border border-ink-300 rounded px-2 py-1 num"
          />
        </label>
        <button
          onClick={() => { setMessage(null); sync.mutate(); }}
          disabled={sync.isPending}
          className="px-3 py-1.5 rounded-md bg-ink-900 text-white text-xs font-semibold hover:bg-ink-800 disabled:opacity-60"
        >
          {sync.isPending ? t('connectors.syncing') : t('connectors.sync')}
        </button>
      </div>
      {message && <p className="text-ink-700">{message}</p>}
      {sync.isSuccess && (
        <div className="flex flex-wrap gap-3 pt-0.5">
          <Link to={`/app/workspace/${wsId}`} className="text-emerald-700 hover:text-emerald-800 font-semibold">
            {t('connectors.view_dashboard', { defaultValue: 'View in dashboard →' })}
          </Link>
          <Link to={`/app/workspace/${wsId}/account-search`} className="text-emerald-700 hover:text-emerald-800 font-semibold">
            {t('connectors.search_accounts', { defaultValue: 'Search accounts →' })}
          </Link>
        </div>
      )}
    </div>
    <ConfirmModal
      open={confirmDisconnect}
      title={t('connectors.disconnect', { defaultValue: 'Disconnect' })}
      body={t('connectors.disconnect_confirm', { defaultValue: 'Disconnect this source? Already-ingested facts are kept.' })}
      confirmLabel={t('connectors.disconnect', { defaultValue: 'Disconnect' })}
      destructive
      onConfirm={() => { disconnect.mutate(); setConfirmDisconnect(false); }}
      onCancel={() => setConfirmDisconnect(false)}
    />
    </>
  );
}
